import React from 'react'
import { AlertTriangle } from 'lucide-react'
import { groupAlerts, resolveLink } from '../lib/alertGroups'
import { isMarketOpen } from '../lib/utils'
import { dailyBarsStale } from '../lib/health'
import { isStopAlert } from '../lib/attention'

// ── System Alerts Panel ────────────────────────────────────────────────────
// Consolidates all critical/warning banners at the top of the dashboard.
// Each alert entry: { id, sev: 'critical'|'warning', title, detail, href?, action? }
//
// dbAlerts (added 2026-08-28) are rows from system_alerts, written by the
// automated monitoring cloud routines (error-sweep, liveness, health-digest,
// exposure) rather than computed here from live health/schwab/ws state. They
// get an ACK button since nothing auto-clears them the way the derived
// alerts above self-clear when the underlying condition resolves.
export default function SystemAlerts({ health, schwabStatus, wsStatus, dbAlerts, onAckGroup, mainSummary, onClearType, onClearAll, recentResolved }) {
  const marketOpen = isMarketOpen()
  const alerts = []

  // ── CRITICAL ──────────────────────────────────────────────────────────────

  if (health?.scheduler_alive === false) {
    alerts.push({
      id: 'scheduler-dead', sev: 'critical',
      title: 'SCHEDULER DEAD — ALL TRADING HALTED',
      // Linux cutover 2026-11-01, Doc 50: wording no longer assumes Railway (T-323)
      detail: 'The trading engine thread has crashed. No data collection, no signal scanning, no trailing stop checks. Restart the trading engine immediately (Linux: the saferfunds_engine container; Railway only if rolled back).',
    })
  }

  if (health?.watchdog_alive === false && health?.scheduler_alive !== false) {
    alerts.push({
      id: 'watchdog-dead', sev: 'critical',
      title: 'WATCHDOG DEAD — SCHEDULER UNMONITORED',
      detail: 'The scheduler watchdog thread has crashed. If the scheduler fails it will NOT auto-restart. Monitor manually or redeploy.',
    })
  }

  // True data stall — scheduler alive but no bars reaching DB (distinct from delayed-feed lag)
  if (health?.data_collection_alert && marketOpen && health?.scheduler_alive !== false) {
    alerts.push({
      id: 'data-stall', sev: 'critical',
      title: 'DATA COLLECTION STALLED',
      // Linux cutover 2026-11-01, Doc 50: wording no longer assumes Railway (T-323)
      detail: health.data_collection_alert + ' — Check the engine logs (Linux: docker logs saferfunds_engine; Railway: railway logs) for Schwab 401 / TCP timeout errors.',
    })
  }

  // ── WARNING ───────────────────────────────────────────────────────────────

  // Schwab REST down → Alpaca delayed fallback
  if (health?.last_collection_source === 'alpaca-delayed') {
    alerts.push({
      id: 'alpaca-fallback', sev: 'warning',
      title: 'SCHWAB REST DOWN — SWITCHED TO ALPACA DELAYED FEED',
      detail: 'Schwab data unavailable. Collection is running on Alpaca (8-15 min bar lag). Signal timing is degraded — all new signals based on delayed prices.',
    })
  }

  // WebSocket FAILED → REST fallback
  if (wsStatus?.status === 'FAILED') {
    const minsSilent = wsStatus.seconds_since_tick != null
      ? Math.round(wsStatus.seconds_since_tick / 60) : null
    alerts.push({
      id: 'ws-failed', sev: 'warning',
      title: 'WEBSOCKET FAILED — SWITCHED TO REST POLLING',
      detail: `No WebSocket tick${minsSilent != null ? ` for ${minsSilent} min` : ''}. System auto-fell back to REST polling. Signal timing degraded until WebSocket recovers.`,
    })
  }

  // WebSocket DEGRADED
  if (wsStatus?.status === 'DEGRADED') {
    const minsSilent = wsStatus.seconds_since_tick != null
      ? Math.round(wsStatus.seconds_since_tick / 60) : null
    const minsLeft = minsSilent != null ? Math.max(0, 5 - minsSilent) : null
    alerts.push({
      id: 'ws-degraded', sev: 'warning',
      title: 'WEBSOCKET DEGRADED — TICKS INTERMITTENT',
      detail: `Last tick${minsSilent != null ? ` ${minsSilent} min ago` : ' missing'}.${minsLeft != null ? ` Auto-switching to REST in ~${minsLeft} min if silence continues.` : ''}`,
    })
  }

  // Schwab auth failure
  if ((schwabStatus?.auth_error_count ?? 0) > 0) {
    const count = schwabStatus.auth_error_count
    alerts.push({
      id: 'schwab-auth', sev: 'warning',
      title: `SCHWAB AUTH FAILURE${count > 1 ? ` — ${count} CONSECUTIVE` : ''}`,
      detail: schwabStatus.auth_error_msg
        || 'Schwab returning 401/403. Auto token-refresh in progress. Re-authorize in Settings if unresolved.',
      href: '/settings', action: 'RE-AUTHORIZE',
    })
  }

  // Daily bars behind (UX review 2026-10-04): the old rule (more than 1 calendar day stale after 17:00 ET) fired every weekend
  // evening although Friday's bar is the right one on a Sunday. dailyBarsStale() knows the weekday and the 16:30 ET job.
  if (dailyBarsStale(health)) {
    alerts.push({
      id: 'daily-stale', sev: 'warning',
      title: `DAILY BARS BEHIND (latest ${health.daily_bars_latest}) — EOD AGGREGATION MAY HAVE FAILED`,
      detail: `tactical_bars_daily last updated ${health.daily_bars_latest ?? 'unknown'}. BLOCK_BUYS_AGAINST_DAILY_TREND is reading stale SMA data. EOD job should have fired at 4:30 PM ET.`,
    })
  }

  // One live row for MAIN positions without a stop-loss. The live broker check is the authority, so the system_alerts rows
  // that only restate it ("... with no protective stop order", often several for the same position) are dropped below while
  // this check is loaded. The row names the symbols, so a different unprotected position is never hidden (best practices 3.17).
  const unprotected = mainSummary && mainSummary.unprotected_count > 0 ? mainSummary : null
  if (unprotected) {
    alerts.push({
      id: 'main-no-stop', sev: 'critical',
      title: `NO STOP-LOSS: ${unprotected.unprotected.slice(0, 5).join(', ')}${unprotected.unprotected_count > 5 ? ` +${unprotected.unprotected_count - 5}` : ''}`,
      detail: 'These MAIN positions have no live stop order at the broker.',
      href: `/positions?s=${unprotected.unprotected[0]}`, action: 'VIEW',
    })
  }

  // The DB-backed monitoring-routine alerts (system_alerts rows) are NOT one banner per row any more (owner, 2026-10-03:
  // "overwhelming", T-118): they are grouped by type into one box with a count per type, a RESOLVE button that opens Claude
  // Code with the fix prompt, and an ACK per type. The derived alerts above keep their own banners (they self-clear).
  const groups = groupAlerts((dbAlerts || []).filter(a => !(unprotected && isStopAlert(a.title))))

  if (!alerts.length && !groups.length) {
    // T-336: nothing open, but show what was just RESOLVED so a cleared alert is confirmed rather than silently gone.
    if (!recentResolved || !recentResolved.length) return null
    return (
      <div className="rounded-lg px-5 py-3" style={{ background: 'rgba(20,83,45,0.35)', border: '1.5px solid rgba(74,222,128,0.5)' }}>
        <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem', fontWeight: 800, color: '#4ade80', letterSpacing: '0.09em' }}>
          ALL CLEAR · {recentResolved.length} RESOLVED IN THE LAST 24 H (latest: {recentResolved[0].title})
        </p>
      </div>
    )
  }

  // One box for everything (owner, 2026-10-04, T-118): the live conditions computed above (scheduler, watchdog, data
  // stall, Schwab, WebSocket, daily bars) are rows with no ACK (they clear themselves when the condition does) and keep
  // their detail text and action link; the system_alerts groups follow with a count and an ACK per type.
  const liveRows = alerts.map(a => ({
    key: a.id, live: true, severity: a.sev, count: 1, sample: a.title, detail: a.detail, href: a.href, action: a.action,
  }))
  const rows = [...liveRows, ...groups.map(g => ({ ...g, live: false }))]
  rows.sort((x, y) => (x.severity === 'critical' ? 0 : 1) - (y.severity === 'critical' ? 0 : 1) || (y.live - x.live) || (y.count - x.count))
  const total = rows.reduce((n, r) => n + r.count, 0)
  const hasCritical = rows.some(r => r.severity === 'critical')
  const tone = hasCritical
    ? { bg: 'linear-gradient(135deg, #3b0000 0%, #450a0a 60%, #3b0000 100%)', border: '2px solid #ef4444', head: '#fef2f2', text: '#fca5a5', accent: '#ef4444' }
    : { bg: 'rgba(92,40,10,0.45)', border: '1.5px solid rgba(245,158,11,0.65)', head: '#fef3c7', text: '#fde68a', accent: '#f59e0b' }
  const mono = { fontFamily: 'JetBrains Mono, monospace' }

  return (
    <div className={`rounded-lg px-5 py-4${hasCritical ? ' alert-critical' : ''}`} style={{ background: tone.bg, border: tone.border }}>
      <div className="flex items-center gap-4 flex-wrap">
        <AlertTriangle style={{ color: tone.text, width: 20, height: 20, flexShrink: 0 }} />
        <p className="flex-1 min-w-0" style={{ ...mono, fontSize: '0.78rem', fontWeight: 800, color: tone.head, letterSpacing: '0.09em' }}>
          {total} OPEN ALERT{total === 1 ? '' : 'S'} · {rows.length} TYPE{rows.length === 1 ? '' : 'S'}
        </p>
        {onClearAll && total > 0 && (
          <button onClick={() => onClearAll()}
            title="Marks every open alert RESOLVED"
            className="px-3 py-1.5 rounded font-bold"
            style={{ ...mono, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.25)', color: tone.text, fontSize: '0.66rem', letterSpacing: '0.1em', whiteSpace: 'nowrap', cursor: 'pointer' }}>
            CLEAR ALL
          </button>
        )}
        <a href={resolveLink()}
          title="Opens Claude Code on this machine with the fix prompt typed in. Nothing is sent until you press Enter."
          className="px-3 py-1.5 rounded font-bold"
          style={{ ...mono, background: tone.accent, color: '#111', fontSize: '0.66rem', letterSpacing: '0.1em', whiteSpace: 'nowrap', textDecoration: 'none' }}>
          RESOLVE
        </a>
      </div>
      <div className="mt-3 space-y-2">
        {rows.map(r => (
          <div key={r.key} title={r.live ? undefined : [r.sample, ...(r.details || [])].join('\n')}>
            <div className="flex items-center gap-3">
              <span style={{ ...mono, fontSize: '0.7rem', fontWeight: 800, color: r.severity === 'critical' ? '#ef4444' : '#f59e0b', minWidth: 34, textAlign: 'right' }}>
                {r.live ? (r.severity === 'critical' ? 'CRIT' : 'LIVE') : `×${r.count}`}
              </span>
              <span className="flex-1 min-w-0 truncate" style={{ ...mono, fontSize: '0.7rem', fontWeight: r.live ? 700 : 400, color: tone.head }}>
                {r.sample || r.title}
                {!r.live && r.subjects && r.subjects.length > 1 && <span style={{ color: tone.head }}> → {r.subjects.join(', ')}</span>}
                {!r.live && <span style={{ color: tone.text, opacity: 0.8 }}> · {r.source}</span>}
              </span>
              {r.href && (
                <a href={r.href} className="px-2 py-0.5 rounded font-bold"
                  style={{ ...mono, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.25)', color: tone.text, fontSize: '0.58rem', letterSpacing: '0.08em', whiteSpace: 'nowrap', textDecoration: 'none' }}>
                  {r.action}
                </a>
              )}
              {!r.live && (
                <button onClick={() => (onClearType ? onClearType(r.source, r.title, r.ids) : onAckGroup?.(r.ids))}
                  title="Marks every open alert of this type RESOLVED (server side, not only the rows shown)"
                  className="px-2 py-0.5 rounded font-bold"
                  style={{ ...mono, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.25)', color: tone.text, fontSize: '0.58rem', letterSpacing: '0.08em', cursor: 'pointer' }}>
                  CLEAR ALL{r.count > 1 ? ` ${r.count}` : ''}
                </button>
              )}
            </div>
            {r.live && r.detail && (
              <p style={{ ...mono, fontSize: '0.63rem', color: tone.text, margin: '2px 0 0 46px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {r.detail}
              </p>
            )}
          </div>
        ))}
      </div>
      {recentResolved && recentResolved.length > 0 && (
        <div className="mt-3 pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.15)' }}>
          <p style={{ ...mono, fontSize: '0.6rem', fontWeight: 800, color: '#4ade80', letterSpacing: '0.09em' }}>
            RESOLVED IN THE LAST 24 H · {recentResolved.length}
          </p>
          {recentResolved.slice(0, 5).map(a => (
            <p key={a.id} className="truncate" style={{ ...mono, fontSize: '0.62rem', color: tone.text, opacity: 0.75, margin: '2px 0 0 0' }}>
              <span style={{ color: '#4ade80', fontWeight: 800 }}>RESOLVED</span> · {a.title} · {a.source}
            </p>
          ))}
        </div>
      )}
    </div>
  )
}
